import { isAbort } from "@jkast90/kast-common/fetch";
import { apiClient } from "./api";
import { putToPresignedUrl } from "./upload";

export interface GalleryPhoto {
  photo_id: string;
  status: "pending" | "approved" | "rejected";
  s3_key: string;
  public_url: string;
  caption: string;
  submitter_name?: string;
  submitter_email?: string;
  created_at: string;
  submission_id?: string | null;
  location_id?: string | null;
  reject_reason?: string;
}

export interface UploadUrlResponse {
  upload_url: string;
  s3_key: string;
  public_url: string;
}

export interface CreatePhotoPayload {
  s3_key: string;
  content_type: string;
  caption: string;
  submission_id: string;
  submitter_name?: string;
  submitter_email?: string;
  location_id?: string;
}

export const galleryService = {
  listApproved: (limit = 100) => apiClient.get<GalleryPhoto[]>("/gallery", { limit }),

  requestUploadUrl: (content_type: string) =>
    apiClient.post<UploadUrlResponse>("/gallery/upload-url", { content_type }),

  createPhoto: (payload: CreatePhotoPayload) => apiClient.post<GalleryPhoto>("/gallery", payload),

  // Admin
  listAll: (photo_status = "all", limit = 200) =>
    apiClient.get<GalleryPhoto[]>("/admin/gallery", { photo_status, limit }),

  updatePhoto: (id: string, updates: Partial<GalleryPhoto>) =>
    apiClient.patch<GalleryPhoto>(`/admin/gallery/${id}`, updates),

  deletePhoto: (id: string) => apiClient.delete<void>(`/admin/gallery/${id}`),
};

export async function uploadPhoto(
  file: File,
  caption: string,
  extras: Omit<CreatePhotoPayload, "s3_key" | "content_type" | "caption">,
): Promise<GalleryPhoto> {
  if (!extras.submission_id) {
    throw new Error("A cleanup event must be picked before uploading a photo.");
  }
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
    throw new Error("Please upload a JPEG, PNG, or WebP image.");
  }
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("Image is larger than 10 MB.");
  }

  const { upload_url, s3_key } = await galleryService.requestUploadUrl(file.type);

  let putResp: Response;
  try {
    putResp = await putToPresignedUrl(upload_url, file);
  } catch (err) {
    throw isAbort(err) ? new Error("Upload timed out. Check your connection and try again.") : err;
  }
  if (!putResp.ok) {
    throw new Error(`Upload failed: ${putResp.status}`);
  }

  return galleryService.createPhoto({
    s3_key,
    content_type: file.type,
    caption,
    ...extras,
  });
}
